/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useMemo } from 'react';
import { useLocation, useNavigation } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { ShopperProducts } from '@/scapi';

export interface CategoryBannerProps {
    category: ShopperProducts.schemas['Category'];
    total?: number;
}

/**
 * Category title block for the PLP top utility bar: heading, optional descriptive
 * copy, and a live product count. No background image — this sits inline alongside
 * the filters button and sort control rather than as a full-bleed hero.
 */
export default function CategoryBanner({ category, total }: CategoryBannerProps) {
    const { t } = useTranslation('category');
    const navigation = useNavigation();
    const location = useLocation();

    const description = category.pageDescription || category.description;

    const isCountPending = useMemo(() => {
        if (navigation.state === 'idle' || !navigation.location) return false;
        if (navigation.location.pathname !== location.pathname) return false;
        const current = new URLSearchParams(location.search);
        const next = new URLSearchParams(navigation.location.search);
        return ['refine', 'sort', 'offset'].some(
            (param) => current.getAll(param).join(',') !== next.getAll(param).join(',')
        );
    }, [navigation.state, navigation.location, location.pathname, location.search]);

    return (
        <div className="min-w-0">
            <h1 className="text-lg md:text-3xl font-stjude-medium-bold leading-tight tracking-[-0.5px]  truncate">
                {category.name || category.id}
            </h1>
            {description && (
                <p className="mt-0.5 text-sm text-muted-foreground line-clamp-2 max-w-2xl">{description}</p>
            )}
            <p className="mt-0.5 text-sm text-muted-foreground" aria-live="polite">
                {isCountPending
                    ? t('banner.counting')
                    : total !== undefined && t('banner.productsAvailable', { count: total })}
            </p>
        </div>
    );
}
